import { assign, Machine } from "xstate";
import { createBrowserHistory } from "history";
import {
  FilterableCounts,
  FilterOptions,
  FilterQuery,
  ProductResult,
  TextParameterMap,
} from "../../../../../microservices/functions/src/types/product_catalog/catalog_api_types";
import {
  fetchInitial,
  fetchProducts,
  fetchProductsAndCounts,
} from "../api/product_catalog_api";
import { categoryFilters } from "../filter/available_filters";
import {
  deserializeFilterFromPath,
  serializeFilterToPath,
} from "../filter/helpers/filter_url_serialization";
import {
  deserializeCurrentUrl,
  getPath,
  observeUrlChanges,
  serializeCurrentFilter,
} from "../helpers/filter_serialization";
import { PaginationData } from "../types";
import deepEqual from "fast-deep-equal";

type ProductCategoryContext = {
  filter: FilterQuery;
  appliedSeoUrlFilters: object;
  baseUrl: string;
  filterOptions: FilterOptions;
  filterableCounts: FilterableCounts;
  allTextParameterValues: TextParameterMap;
  products: ProductResult[];
  bestsellers: ProductResult[];
  pagination: PaginationData;
  error?: {
    message: string;
    details: any;
  };
  productsListElement?: HTMLElement;
};

export type AlterFilterEvent = {
  type: "ALTER_FILTER";
  newFilter: FilterQuery;
};

export const CatalogMachine = Machine<ProductCategoryContext>(
  {
    id: "productCategory",
    initial: "loadingInitialView",
    states: {
      loadingInitialView: {
        entry: "fetchInitialFilterFromUrl",
        invoke: {
          id: "fetchInitial",
          src: "fetchInitial",
          onDone: {
            target: "productsDisplayed.idle",
            actions: [
              assign({
                filterOptions: (context, event) => event.data.filterOptions,
                allTextParameterValues: (context, event) =>
                  event.data.allTextParameterValues,
              }),
              "replaceProducts",
              "replaceBestsellers",
              "assignFilterableCounts",
              "assignResultsCount",
            ],
          },
          onError: {
            target: "initializationFailed",
            actions: "assignError",
          },
        },
      },
      productsDisplayed: {
        initial: "loadingWithNewFilter",
        on: {
          ALTER_FILTER: {
            target: ".loadingWithNewFilter",
            actions: [
              "resetCurrentPage",
              "assignNewFilter",
              "serializeFilterToUrl",
            ],
          },
          RESET_FILTER: {
            target: ".loadingWithNewFilter",
            actions: [
              "resetCurrentPage",
              "resetFilter",
              "serializeFilterToUrl",
            ],
          },
        },
        invoke: {
          id: "urlChangeObserver",
          src: "observeUrlChanges",
        },
        states: {
          idle: {
            on: {
              GO_TO_PAGE: {
                target: "loadingPage",
                actions: [
                  "scrollToTopOfProductsList",
                  "goToPage",
                  "serializeFilterToUrl",
                ],
              },
              LOAD_MORE_RESULTS: {
                target: "loadingMoreResults",
                actions: ["increasePageNumber", "loadMoreProducts"],
              },
            },
          },
          loadingWithNewFilter: {
            invoke: {
              id: "fetchProducts",
              src: "fetchProductsAndCounts",
              onDone: {
                target: "idle",
                actions: [
                  "assignFilterableCounts",
                  "replaceProducts",
                  "replaceBestsellers",
                  "assignResultsCount",
                ],
              },
              onError: {
                target: "loadingFullPageFailed",
                actions: "assignError",
              },
            },
          },
          loadingPage: {
            invoke: {
              id: "fetchProducts",
              src: "fetchProducts",
              onDone: {
                target: "idle",
                actions: "replaceProducts",
              },
              onError: {
                target: "loadingFullPageFailed",
                actions: "assignError",
              },
            },
          },
          loadingMoreResults: {
            invoke: {
              id: "fetchMoreProducts",
              src: "fetchProducts",
              onDone: {
                target: "idle",
                actions: "appendProducts",
              },
              onError: {
                target: "idle",
                actions: "assignError",
              },
            },
          },
          loadingFullPageFailed: {
            on: {
              RETRY: {
                target: "loadingPage",
                actions: "removeError",
              },
            },
          },
        },
      },
      initializationFailed: {
        on: {
          RETRY: {
            target: "loadingInitialView",
            actions: "removeError",
          },
        },
      },
    },
  },
  {
    actions: {
      assignError: assign({
        error: (context, event) => ({
          message: event.data?.message ?? "Unknown error",
          details: event.data?.details ?? {},
        }),
      }),
      removeError: assign({
        error: (_context, _event) => undefined,
      }),
      assignNewFilter: assign({
        filter: (context, event) => event.newFilter,
      }),
      resetFilter: assign({
        filter: (context, _event) =>
          ({
            categoryId: context.filter.categoryId,
            sort: context.filter.sort,
          } as FilterQuery),
      }),
      assignResultsCount: assign({
        pagination: (context, event) =>
          ({
            ...context.pagination,
            resultsCount: event.data.pagination.resultsCount,
          } as PaginationData),
      }),
      assignFilterableCounts: assign({
        filterableCounts: (context, event) => event.data.filterableCounts,
      }),
      replaceProducts: assign({
        products: (context, event) => event.data.products,
      }),
      appendProducts: assign({
        products: (context, event) => [
          ...context.products,
          ...event.data.products,
        ],
      }),
      replaceBestsellers: assign({
        bestsellers: (context, event) => event.data.bestsellers,
      }),
      goToPage: assign({
        pagination: (context, event) => ({
          ...context.pagination,
          currentPage: event.page,
        }),
      }),
      increasePageNumber: assign({
        pagination: (context, _event) => ({
          ...context.pagination,
          currentPage: context.pagination.currentPage + 1,
        }),
      }),
      resetCurrentPage: assign({
        pagination: (context, _event) => ({
          ...context.pagination,
          currentPage: 1,
        }),
      }),
      serializeFilterToUrl: (context, _event) => {
        const newPath = serializeCurrentFilter(context.filter);
        const currentPath = getPath();
        if (newPath !== currentPath) {
          window.history.pushState(null, "", newPath);
        }
      },
      fetchInitialFilterFromUrl: assign({
        filter: (context, _event) =>
          deserializeCurrentUrl(
            context.filter.categoryId,
            context.appliedSeoUrlFilters
          ),
      }),
    },
    services: {
      fetchInitial: (context, _event) =>
        fetchInitial(context.baseUrl, context.filter, context.pagination),
      fetchProducts: (context, _event) =>
        fetchProducts(context.baseUrl, context.filter, context.pagination),
      fetchProductsAndCounts: (context, _event) =>
        fetchProductsAndCounts(
          context.baseUrl,
          context.filter,
          context.pagination
        ),
      observeUrlChanges: observeUrlChanges,
    },
  }
);
